import classNames from "classnames";
import { useState } from "react";
import { useHistory, useParams } from "react-router";
import { common_utils } from "../../../../../../../common/common_utils";
import { Constants } from "../../../../../../../common/constants";
import {
	DeleteRequest,
	DiscordAuthToken,
	IDeleteEventResult,
} from "../../../../../../../common/sesh_types";
import { RouterInferenceHelpers } from "../../../../../../../src/api/trpc/rootRouter";
import { DashboardUtils } from "../../../../dashboard_utils";
import { trpc } from "../../../../utils/trpc";
import { RemovingThrobber } from "../../../removing_throbber";
import { DisableableLink } from "../../base_components/disableable_link";
import { LinkWithPreviousPage } from "../../base_components/link_with_prev_page";
import { useChannelName } from "../../hooks/use_channel_name";
import { useIsAdmin } from "../../hooks/use_is_admin";
import { useUserContext } from "../../hooks/use_user_context";
import MemberDisplayNameWithTooltip from "../../member_display_name/member_display_name_with_tooltip";
import { WithTooltip } from "../../tooltips/with_tooltip";
import { ListingsFormTableData } from "../listings_form_table_data";
import { DateTableData } from "./date_table_data";

export default function PollRow(props: { message_id: string }) {
	const { server_id } = useParams<{ server_id: string }>();

	const history = useHistory();

	const poll_query = trpc.poll.getListItem.useQuery(
		{
			guild_id: server_id,
			message_id: props.message_id,
		},
		{
			staleTime: Infinity,
		}
	);

	const channel_name = useChannelName(poll_query.data?.channel_id);

	if (!poll_query.isSuccess) {
		return <></>;
	}

	const poll = poll_query.data;

	const now_date = new Date();

	const is_in_past = poll.end_time && new Date(poll.end_time) < now_date;

	const classString = is_in_past ? "opacity-75" : "";

	return (
		<tr className={classString}>
			<DateTableData
				start_time_js_date={new Date(poll.created_at)}
				end_time_js_date={new Date(poll.created_at)}
				repeat_data={undefined}
			/>
			<ListingsFormTableData content={poll.poll_name} />
			<ListingsFormTableData content={channel_name} />
			<td>
				<MemberDisplayNameWithTooltip
					user_id={poll.author_id}
					tooltip_type="SeshTooltip"
				/>
			</td>
			<td>
				<DisableableLink
					className="manual-link"
					to={common_utils.getPollResultsURL(
						server_id,
						poll.message_id
					)}
				>
					<i className="fas fa-poll"></i> {poll.vote_count}
				</DisableableLink>
			</td>
			<td>
				<EditAction guild_id={server_id} poll={poll} />
			</td>
		</tr>
	);
}

function EditAction(props: {
	guild_id: string;
	poll: RouterInferenceHelpers["poll"]["getListItem"]["output"];
}) {
	const [is_removing, setIsRemoving] = useState(false);

	const user = useUserContext();
	const is_admin_query = useIsAdmin();

	const context = trpc.useContext();

	if (is_removing || !is_admin_query.isSuccess) {
		return (
			<div className="flex w-full flex-col items-center">
				<RemovingThrobber />
			</div>
		);
	}

	const is_poll_creator = user?.id === props.poll.author_id;
	const has_edit_poll_perms = is_admin_query.data || is_poll_creator;
	const has_poll_ended =
		props.poll.end_time && new Date(props.poll.end_time) < new Date();

	const cannot_edit_event_tooltip_text =
		"Must be event creator or have Manage Sesh permissions to modify events";

	const edit_disabled = has_poll_ended || !has_edit_poll_perms;

	return (
		<div className="flex w-full flex-col items-center">
			<div className="flex w-full flex-wrap justify-center gap-6 sm:gap-4">
				<DisableableLink
					to={common_utils.getPollEditURL(
						props.guild_id,
						props.poll.message_id
					)}
					is_disabled={edit_disabled}
					tooltip={
						has_poll_ended
							? "Can not edit completed or ongoing polls"
							: has_edit_poll_perms
							? "Edit poll"
							: cannot_edit_event_tooltip_text
					}
				>
					<i className="fas fa-pencil-alt"></i>
				</DisableableLink>
				<WithTooltip
					style="Info"
					tooltip="View in Discord"
					disable_click
				>
					<a
						className="manual-link"
						href={`${Constants.DISCORD_LINK_PREFIX}${props.guild_id}/${props.poll.channel_id}/${props.poll.message_id}`}
						target="_blank"
					>
						<i className="fas fa-external-link-alt" />
					</a>
				</WithTooltip>
				<WithTooltip
					style="Info"
					tooltip={
						has_edit_poll_perms
							? "Delete poll"
							: cannot_edit_event_tooltip_text
					}
					disable_click
				>
					<button
						className={classNames({
							"input-disabled text-gray-400":
								!has_edit_poll_perms,
							"text-warning hover:text-warning-hover":
								has_edit_poll_perms,
						})}
						onClick={() => {
							if (!has_edit_poll_perms) {
								return;
							}
							onDeleteClicked(
								context,
								props.poll,
								props.guild_id,
								user,
								setIsRemoving
							);
						}}
					>
						<i className="far fa-trash-alt" />
					</button>
				</WithTooltip>
			</div>
		</div>
	);
}

async function onDeleteClicked(
	context: ReturnType<typeof trpc.useContext>,
	poll: RouterInferenceHelpers["poll"]["getListItem"]["output"],
	guild_id: string,
	auth_token: DiscordAuthToken,
	setPollPendingDelete: (is_deleting: boolean) => void
) {
	const confirmation_result = await DashboardUtils.ShowDeleteConfirmAlert(
		<span>
			Are you sure you want to permanently delete poll{" "}
			<span className="has-text-weight-bold">{poll.poll_name}</span>?
		</span>
	);

	if (!confirmation_result.isConfirmed) {
		return;
	}

	setPollPendingDelete(true);

	deletePoll(auth_token, guild_id, poll.message_id).then((val) => {
		setPollPendingDelete(false);
		if (val.db_success) {
			context.poll.list.invalidate();
		}
	});
}

async function deletePoll(
	auth_token: DiscordAuthToken,
	guild_id: string,
	message_id: string
) {
	const request_body: DeleteRequest = {
		access_token: auth_token.access_token,
		token_type: auth_token.token_type,
		guild_id,
		message_id,
	};

	return fetch("/api/delete_poll", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request_body),
	}).then((response) =>
		response.json().then((value: IDeleteEventResult) => {
			if (value.err) {
				DashboardUtils.ShowDefaultErrorAlert(value.err);
			}
			return value;
		})
	);
}
